import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Search,
  ArrowLeft,
  ShoppingBag,
  ShoppingCart,
  Plus,
  Pill,
  Loader2,
  X,
  Check,
  AlertCircle,
  Grid3X3,
  List,
  HelpCircle,
  Send
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PharmacyCatalogPage = ({ 
  onNavigate, 
  initialSearch = '', 
  initialCategory = '',
  cart = [],
  onAddToCart 
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || 'all');
  const [selectedAvailability, setSelectedAvailability] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 24;

  // Product request modal state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [requestForm, setRequestForm] = useState({
    requester_name: '',
    phone: '',
    email: '',
    products: [{ name: '', quantity: 1 }], // Support multi-products
    description: '',
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  
  // Product detail modal state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);

  // Function to open request modal with search query pre-filled
  const openRequestModal = () => {
    setRequestForm(prev => ({
      ...prev,
      products: searchQuery.trim() 
        ? [{ name: searchQuery.trim(), quantity: 1 }]
        : [{ name: '', quantity: 1 }]
    }));
    setShowRequestModal(true);
  };
  
  // Function to view product details
  const viewProductDetail = (product) => {
    setSelectedProduct(product);
    setShowProductModal(true);
  };

  // Add another product to the request
  const addProductToRequest = () => {
    setRequestForm(prev => ({
      ...prev,
      products: [...prev.products, { name: '', quantity: 1 }]
    }));
  };

  // Remove a product from the request
  const removeProductFromRequest = (index) => {
    if (requestForm.products.length > 1) {
      setRequestForm(prev => ({
        ...prev,
        products: prev.products.filter((_, i) => i !== index)
      }));
    }
  };

  // Update a product in the request
  const updateProductInRequest = (index, field, value) => {
    setRequestForm(prev => ({
      ...prev,
      products: prev.products.map((p, i) => 
        i === index ? { ...p, [field]: value } : p
      )
    }));
  };

  const logoUrl = 'https://customer-assets.emergentagent.com/job_skincare-check-1/artifacts/6cemh04i_LOGO%20DEF.jpg';

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/pharmacy/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: ITEMS_PER_PAGE,
        skip: reset ? 0 : (page - 1) * ITEMS_PER_PAGE,
        sort: sortBy
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedAvailability !== 'all') params.append('availability', selectedAvailability);

      const response = await axios.get(`${API_URL}/pharmacy/products?${params}`);
      
      // API returns { products: [...], total: ..., skip: ..., limit: ... }
      const productsData = response.data.products || response.data;
      const totalProducts = response.data.total || productsData.length;
      
      if (reset) {
        setProducts(productsData);
      } else {
        setProducts(prev => [...prev, ...productsData]);
      }
      
      setHasMore(productsData.length === ITEMS_PER_PAGE && products.length + productsData.length < totalProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, searchQuery, selectedCategory, selectedAvailability, products.length]);

  // Initial load
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch products when filters change
  useEffect(() => {
    fetchProducts(true);
  }, [selectedCategory, selectedAvailability, sortBy, fetchProducts]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchProducts(true);
  };

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
    fetchProducts();
  };

  const getAvailabilityStatus = (stock) => {
    // Affichage public : binaire pour rester conforme à la déontologie
    // pharmaceutique (pas de niveau de stock détaillé, pas de prix public).
    if (stock > 0) {
      return { label: 'Disponible', color: 'bg-green-50 text-green-700 border-green-200' };
    }
    return { label: 'Rupture momentanée', color: 'bg-red-50 text-red-700 border-red-200' };
  };

  const isInCart = (productId) => cart.some(item => item.id === productId);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    
    // Filter out empty products
    const validProducts = requestForm.products.filter(p => p.name.trim());
    
    if (!requestForm.requester_name || !requestForm.phone || validProducts.length === 0) {
      toast.error('Veuillez remplir les champs obligatoires et ajouter au moins un produit');
      return;
    }
    
    setSubmittingRequest(true);
    try {
      // Format products as a string for the API
      const productNames = validProducts.map(p => 
        p.quantity > 1 ? `${p.name} (x${p.quantity})` : p.name
      ).join(', ');
      
      await axios.post(`${API_URL}/pharmacy/product-requests`, {
        requester_name: requestForm.requester_name,
        phone: requestForm.phone,
        email: requestForm.email,
        product_name: productNames,
        description: requestForm.description,
      });
      
      const productCount = validProducts.length;
      toast.success(`Votre demande pour ${productCount} produit${productCount > 1 ? 's' : ''} a bien été envoyée.`);
      // Show success view with Wave payment option (instead of closing immediately)
      setRequestSubmitted(true);
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Erreur lors de l\'envoi de la demande');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const filteredProducts = useMemo(() => {
    let result = [...products];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.name?.toLowerCase().includes(query) ||
        p.brand?.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [products, searchQuery]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF8F3] to-white">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-[#E8DDD4] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onNavigate('home')}
                className="text-[#8B7355]"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img 
                src={logoUrl} 
                alt="Logo" 
                className="h-8 w-auto object-contain cursor-pointer"
                onClick={() => onNavigate('home')}
              />
              <h1 className="font-bold text-[#5D4E37] text-lg hidden sm:block">Catalogue</h1>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onNavigate('cart')}
              className="relative text-[#8B7355]"
              data-testid="cart-btn"
            >
              <ShoppingBag className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C45C26] text-white text-xs rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Search and Filters */}
      <div className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#B8A089]" />
          <Input
            type="text"
            placeholder="Rechercher un produit..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 pr-24 py-5 rounded-xl border-2 border-[#E8DDD4] focus:border-[#C45C26] bg-white"
            data-testid="search-input"
          />
          <Button
            type="submit"
            size="sm"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#C45C26] hover:bg-[#A84B1F]"
          >
            Rechercher
          </Button>
        </form>

        {/* Filters Row */}
        <div className="flex flex-wrap gap-3 items-center">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-40 bg-white border-[#E8DDD4]">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedAvailability} onValueChange={setSelectedAvailability}>
            <SelectTrigger className="w-36 bg-white border-[#E8DDD4]">
              <SelectValue placeholder="Disponibilité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="in_stock">Disponibles</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-36 bg-white border-[#E8DDD4]">
              <SelectValue placeholder="Trier par" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Nom A-Z</SelectItem>
              <SelectItem value="-name">Nom Z-A</SelectItem>
            </SelectContent>
          </Select>

          <div className="ml-auto flex gap-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'bg-[#C45C26]' : 'text-[#8B7355]'}
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-[#C45C26]' : 'text-[#8B7355]'}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Active filters */}
        {(selectedCategory !== 'all' || selectedAvailability !== 'all' || searchQuery) && (
          <div className="flex flex-wrap gap-2">
            {searchQuery && (
              <Badge variant="outline" className="bg-[#FDF8F3] border-[#E8DDD4] text-[#5D4E37]">
                Recherche: {searchQuery}
                <button onClick={() => { setSearchQuery(''); fetchProducts(true); }} className="ml-2">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {selectedCategory !== 'all' && (
              <Badge variant="outline" className="bg-[#FDF8F3] border-[#E8DDD4] text-[#5D4E37]">
                {selectedCategory}
                <button onClick={() => setSelectedCategory('all')} className="ml-2">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {selectedAvailability !== 'all' && (
              <Badge variant="outline" className="bg-[#FDF8F3] border-[#E8DDD4] text-[#5D4E37]">
                {selectedAvailability === 'in_stock' ? 'En stock' : 'Stock limité'}
                <button onClick={() => setSelectedAvailability('all')} className="ml-2">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Products Grid/List */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        {/* Product Request Banner */}
        <Card className="mb-6 bg-gradient-to-r from-[#FDF8F3] to-[#F5EDE4] border-[#E8DDD4]">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-[#5D4E37] text-sm text-center sm:text-left">
                Consultez une sélection de nos produits. Si vous ne trouvez pas ce que vous cherchez, contactez-nous !
              </p>
              <Button
                variant="outline"
                onClick={openRequestModal}
                className="border-[#C45C26] text-[#C45C26] hover:bg-[#C45C26] hover:text-white whitespace-nowrap"
                data-testid="product-request-btn"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Vous ne trouvez pas ? Faites une demande
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading && products.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#C45C26]" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <Card className="bg-white border-[#E8DDD4]">
            <CardContent className="p-12 text-center">
              <AlertCircle className="w-16 h-16 mx-auto mb-4 text-[#E8DDD4]" />
              <h3 className="text-lg font-semibold text-[#5D4E37] mb-2">Aucun produit trouvé</h3>
              <p className="text-[#8B7355] mb-4">Essayez avec d'autres critères de recherche</p>
              <Button
                variant="outline"
                onClick={openRequestModal}
                className="border-[#C45C26] text-[#C45C26] hover:bg-[#C45C26] hover:text-white"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Faites une demande de produit
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-sm text-[#8B7355] mb-4">
              {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} trouvé{filteredProducts.length > 1 ? 's' : ''}
            </p>
            
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4' 
              : 'space-y-3'
            }>
              {filteredProducts.map((product, index) => {
                const availability = getAvailabilityStatus(product.stock_total);
                const inCart = isInCart(product.id);
                const uniqueKey = product.id || `product-${index}`;
                
                return viewMode === 'grid' ? (
                  <Card
                    key={uniqueKey}
                    className="cursor-pointer hover:shadow-lg transition-all border-[#E8DDD4] bg-white overflow-hidden group"
                    data-testid={`product-card-${product.id}`}
                  >
                    <CardContent className="p-4">
                      <div 
                        onClick={() => viewProductDetail(product)}
                        className="cursor-pointer"
                      >
                        <div className="w-full h-24 bg-gradient-to-br from-[#FDF8F3] to-[#F5EDE4] rounded-lg mb-3 flex items-center justify-center group-hover:from-[#F5EDE4] group-hover:to-[#EDE3D9] transition-colors overflow-hidden">
                          {product.image_url ? (
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="w-full h-full object-contain"
                              loading="lazy"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'block'; }}
                            />
                          ) : null}
                          <Pill
                            className="w-10 h-10 text-[#C45C26]/40"
                            style={{ display: product.image_url ? 'none' : 'block' }}
                          />
                        </div>
                        <h4 className="font-medium text-[#5D4E37] text-sm line-clamp-2 mb-1 min-h-[2.5rem]">
                          {product.name}
                        </h4>
                        {product.brand && (
                          <p className="text-xs text-[#8B7355] mb-1">{product.brand}</p>
                        )}
                        <Badge variant="outline" className={`mt-2 text-xs ${availability.color}`}>
                          {availability.label}
                        </Badge>
                        {product.regulatory_category === 'medicament' && (
                          <Badge
                            variant="outline"
                            className="mt-1 text-[10px] bg-rose-50 border-rose-200 text-rose-700"
                            data-testid={`badge-medicament-${product.id}`}
                          >
                            Médicament · comptoir
                          </Badge>
                        )}
                        {product.regulatory_category === 'parapharmacie' && (
                          <Badge
                            variant="outline"
                            className="mt-1 text-[10px] bg-emerald-50 border-emerald-200 text-emerald-700"
                            data-testid={`badge-parapharmacie-${product.id}`}
                          >
                            Parapharmacie
                          </Badge>
                        )}
                      </div>
                      
                      <Button
                        size="sm"
                        className={`w-full mt-3 ${inCart 
                          ? 'bg-[#C45C26]/20 text-[#C45C26] hover:bg-[#C45C26]/30' 
                          : 'bg-[#C45C26] hover:bg-[#A84B1F] text-white'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!inCart && product.stock_total > 0) {
                            onAddToCart(product);
                            toast.success('Ajouté à ma demande');
                          }
                        }}
                        disabled={product.stock_total === 0}
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        {inCart ? (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Dans ma demande
                          </>
                        ) : product.stock_total === 0 ? (
                          'Indisponible'
                        ) : (
                          <>
                            <Plus className="w-4 h-4 mr-1" />
                            Demander
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <Card
                    key={uniqueKey}
                    className="cursor-pointer hover:shadow-md transition-all border-[#E8DDD4] bg-white"
                    data-testid={`product-card-${product.id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div 
                        className="w-16 h-16 bg-gradient-to-br from-[#FDF8F3] to-[#F5EDE4] rounded-lg flex items-center justify-center flex-shrink-0 cursor-pointer overflow-hidden"
                        onClick={() => viewProductDetail(product)}
                      >
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-full h-full object-contain"
                            loading="lazy"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'block'; }}
                          />
                        ) : null}
                        <Pill
                          className="w-8 h-8 text-[#C45C26]/40"
                          style={{ display: product.image_url ? 'none' : 'block' }}
                        />
                      </div>
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => viewProductDetail(product)}>
                        <h4 className="font-medium text-[#5D4E37] truncate">{product.name}</h4>
                        <p className="text-sm text-[#8B7355]">{product.brand}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={`text-xs ${availability.color}`}>
                            {availability.label}
                          </Badge>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className={inCart 
                          ? 'bg-[#C45C26]/20 text-[#C45C26] hover:bg-[#C45C26]/30' 
                          : 'bg-[#C45C26] hover:bg-[#A84B1F] text-white'
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!inCart && product.stock_total > 0) {
                            onAddToCart(product);
                            toast.success('Ajouté à ma demande');
                          }
                        }}
                        disabled={product.stock_total === 0}
                      >
                        {inCart ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center mt-8">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="border-[#E8DDD4] text-[#C45C26] hover:bg-[#FDF8F3]"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Charger plus de produits
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Product Request Modal */}
      <Dialog
        open={showRequestModal}
        onOpenChange={(open) => {
          setShowRequestModal(open);
          if (!open) {
            // Reset success view + form when dialog is closed
            setRequestSubmitted(false);
            setRequestForm({
              requester_name: '',
              phone: '',
              email: '',
              products: [{ name: '', quantity: 1 }],
              description: '',
            });
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          {requestSubmitted ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#5D4E37] flex items-center gap-2">
                  <span className="inline-flex w-8 h-8 rounded-full bg-green-100 items-center justify-center">
                    <svg className="w-4 h-4 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  Demande bien reçue, merci !
                </DialogTitle>
                <DialogDescription className="text-[#8B7355]">
                  Notre équipe vous recontacte rapidement par téléphone pour la disponibilité, le délai et le tarif.
                </DialogDescription>
              </DialogHeader>

              <div
                className="bg-[#FDF8F3] border border-[#E8DDD4] rounded-xl p-4 text-sm text-[#5D4E37] leading-relaxed"
                data-testid="success-info-block"
              >
                <p>
                  Notre équipe vérifie la disponibilité et vous recontacte par téléphone
                  pour confirmer la mise à disposition. Le retrait se fait au comptoir
                  après validation pharmaceutique.
                </p>
                <p className="text-[11px] text-[#8B7355] italic mt-2 pt-2 border-t border-[#E8DDD4]">
                  Cette demande ne constitue pas une vente en ligne. La délivrance reste
                  soumise à la validation de l'équipe officinale.
                </p>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRequestModal(false);
                    setRequestSubmitted(false);
                  }}
                  className="w-full"
                  data-testid="request-close-btn"
                >
                  Fermer
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#5D4E37]">Demande de produit</DialogTitle>
                <DialogDescription className="text-[#8B7355]">
                  Vous ne trouvez pas votre produit ? Faites une demande et nous vous contacterons.
                </DialogDescription>
              </DialogHeader>
          
          <form onSubmit={handleRequestSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="requester_name" className="text-[#5D4E37]">
                Nom du demandeur <span className="text-red-500">*</span>
              </Label>
              <Input
                id="requester_name"
                value={requestForm.requester_name}
                onChange={(e) => setRequestForm({ ...requestForm, requester_name: e.target.value })}
                placeholder="Votre nom"
                className="border-[#E8DDD4] focus:border-[#C45C26]"
                required
                data-testid="request-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#5D4E37]">
                Téléphone <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                value={requestForm.phone}
                onChange={(e) => setRequestForm({ ...requestForm, phone: e.target.value })}
                placeholder="07 00 00 00 00"
                className="border-[#E8DDD4] focus:border-[#C45C26]"
                required
                data-testid="request-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#5D4E37]">
                Email <span className="text-[#8B7355] text-sm">(optionnel)</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={requestForm.email}
                onChange={(e) => setRequestForm({ ...requestForm, email: e.target.value })}
                placeholder="votre@email.com"
                className="border-[#E8DDD4] focus:border-[#C45C26]"
                data-testid="request-email-input"
              />
            </div>

            {/* Multi-product section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-[#5D4E37]">
                  Produit(s) recherché(s) <span className="text-red-500">*</span>
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={addProductToRequest}
                  className="text-[#C45C26] hover:text-[#A84B1F] h-7 px-2"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter un produit
                </Button>
              </div>
              
              {requestForm.products.map((product, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={product.name}
                    onChange={(e) => updateProductInRequest(index, 'name', e.target.value)}
                    placeholder={index === 0 ? "Ex: Doliprane 1000mg" : "Autre produit..."}
                    className="flex-1 border-[#E8DDD4] focus:border-[#C45C26]"
                    data-testid={`request-product-input-${index}`}
                  />
                  <Select
                    value={product.quantity.toString()}
                    onValueChange={(value) => updateProductInRequest(index, 'quantity', parseInt(value))}
                  >
                    <SelectTrigger className="w-20 border-[#E8DDD4]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {requestForm.products.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeProductFromRequest(index)}
                      className="h-9 w-9 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              {searchQuery && requestForm.products[0]?.name === searchQuery && (
                <p className="text-xs text-[#2E7D32] flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Pré-rempli avec votre recherche
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-[#5D4E37]">
                Description ou précision <span className="text-[#8B7355] text-sm">(optionnel)</span>
              </Label>
              <Textarea
                id="description"
                value={requestForm.description}
                onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
                placeholder="Précisions sur les produits recherchés..."
                className="border-[#E8DDD4] focus:border-[#C45C26] min-h-[80px]"
                data-testid="request-description-input"
              />
            </div>

            {/* Pre-payment opt-in (Wave + Orange Money) */}
            {/* SUPPRIMÉ — Conformité réglementaire CI :
                le paiement à distance ne doit pas être proposé au client
                depuis le formulaire public. Il est déclenché manuellement
                par l'équipe officinale, et seulement sur les produits de
                parapharmacie validés et prêts. */}

            <Button
              type="submit"
              className="w-full bg-[#C45C26] hover:bg-[#A84B1F]"
              disabled={submittingRequest}
              data-testid="submit-request-btn"
            >
              {submittingRequest ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer la demande de mise à disposition
                </>
              )}
            </Button>

            <p
              className="text-[11px] text-[#8B7355] leading-relaxed text-center px-2"
              data-testid="request-legal-notice"
            >
              Cette demande ne constitue pas une vente en ligne. La mise à disposition
              et la délivrance des produits de santé restent soumises à la validation
              de l'équipe officinale.
            </p>
          </form>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Product Detail Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="sm:max-w-[500px]" aria-describedby="product-detail-description">
          <DialogHeader>
            <DialogTitle className="text-[#5D4E37]">
              Détails du produit
            </DialogTitle>
            <DialogDescription id="product-detail-description" className="sr-only">
              Informations détaillées sur le produit sélectionné
            </DialogDescription>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="space-y-4">
              {/* Product Image */}
              <div className="w-full h-48 bg-gradient-to-br from-[#FDF8F3] to-[#F5EDE4] rounded-lg flex items-center justify-center overflow-hidden">
                {selectedProduct.image_url ? (
                  <img
                    src={selectedProduct.image_url}
                    alt={selectedProduct.name}
                    className="w-full h-full object-contain"
                    data-testid="product-detail-image"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }}
                  />
                ) : null}
                <Pill
                  className="w-16 h-16 text-[#C45C26]/40"
                  style={{ display: selectedProduct.image_url ? 'none' : 'block' }}
                />
              </div>
              
              {/* Product Info */}
              <div className="space-y-3">
                <h3 className="text-xl font-semibold text-[#5D4E37]">{selectedProduct.name}</h3>
                
                {selectedProduct.brand && (
                  <p className="text-[#8B7355]">Marque : {selectedProduct.brand}</p>
                )}
                
                {selectedProduct.category && (
                  <Badge variant="outline" className="text-[#C45C26] border-[#C45C26]">
                    {selectedProduct.category}
                  </Badge>
                )}

                {selectedProduct.regulatory_category === 'medicament' && (
                  <div
                    className="bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-xs text-rose-800 flex items-start gap-2"
                    data-testid="regulatory-medicament-notice"
                  >
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      <strong>Médicament</strong> — la délivrance se fait
                      uniquement au comptoir avec conseil pharmacien.
                      Aucun lien de paiement à distance pour ce produit.
                    </span>
                  </div>
                )}
                {selectedProduct.regulatory_category === 'parapharmacie' && (
                  <div
                    className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-xs text-emerald-800 flex items-start gap-2"
                    data-testid="regulatory-parapharmacie-notice"
                  >
                    <ShoppingBag className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      <strong>Parapharmacie</strong> — paiement à distance
                      possible (Wave / Orange Money) après validation.
                    </span>
                  </div>
                )}
                
                <div className="flex items-center justify-end py-3 border-t border-b border-[#E8DDD4]">
                  <Badge 
                    variant="outline" 
                    className={selectedProduct.stock_total > 0 ? 'text-green-600 border-green-600' : 'text-red-600 border-red-600'}
                  >
                    {selectedProduct.stock_total > 0 ? 'Disponible' : 'Rupture momentanée'}
                  </Badge>
                </div>
                
                {selectedProduct.is_prescription_only && (
                  <div
                    className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800 flex items-start gap-2"
                    data-testid="prescription-notice"
                  >
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      Médicament à prescription. Présentez votre ordonnance au comptoir
                      lors du retrait.
                    </span>
                  </div>
                )}

                {/* Description enrichie (validée pharmacien) */}
                {selectedProduct.description_long && (
                  <div className="bg-white border border-[#E8DDD4] rounded-lg p-3" data-testid="product-description-long">
                    <p className="text-sm text-[#5D4E37] leading-relaxed">
                      {selectedProduct.description_long}
                    </p>
                  </div>
                )}

                {/* Conseils d'utilisation */}
                {selectedProduct.usage_advice && (
                  <div className="bg-[#FDF8F3] border border-[#E8DDD4] rounded-lg p-3" data-testid="product-usage-advice">
                    <p className="text-xs font-semibold text-[#5D4E37] uppercase tracking-wide mb-1">
                      Conseils d'utilisation
                    </p>
                    <p className="text-sm text-[#5D4E37] leading-relaxed">
                      {selectedProduct.usage_advice}
                    </p>
                  </div>
                )}

                {/* Lien notice ANSM officielle */}
                {selectedProduct.notice_url && (
                  <a
                    href={selectedProduct.notice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-[#C45C26] hover:text-[#A84B1F] hover:underline font-medium"
                    data-testid="product-notice-link"
                  >
                    <HelpCircle className="w-4 h-4" />
                    Consulter la notice officielle
                  </a>
                )}

                {/* Disclaimer if any enriched content present */}
                {(selectedProduct.description_long || selectedProduct.usage_advice) && (
                  <p className="text-[10px] text-[#8B7355] italic leading-relaxed pt-1 border-t border-[#E8DDD4]">
                    En cas de doute, demandez conseil à votre pharmacien. Ces informations
                    ne se substituent pas à la notice officielle ni à un avis médical.
                  </p>
                )}
                
                {selectedProduct.dosage && (
                  <p className="text-sm text-[#78716C]">
                    <span className="font-medium text-[#5D4E37]">Dosage :</span> {selectedProduct.dosage}
                  </p>
                )}
                
                {selectedProduct.form && (
                  <p className="text-sm text-[#78716C]">
                    <span className="font-medium text-[#5D4E37]">Forme :</span> {selectedProduct.form}
                  </p>
                )}
                
                {selectedProduct.description && !selectedProduct.description_long && (
                  <p className="text-sm text-[#78716C]">{selectedProduct.description}</p>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  className="flex-1 bg-[#C45C26] hover:bg-[#A84B1F]"
                  disabled={selectedProduct.stock_total === 0}
                  onClick={() => {
                    if (selectedProduct.stock_total > 0) {
                      onAddToCart(selectedProduct);
                      toast.success('Ajouté à votre liste de demandes');
                      setShowProductModal(false);
                    }
                  }}
                  data-testid="add-to-cart-detail-btn"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {selectedProduct.stock_total > 0 ? 'Ajouter à ma demande' : 'Indisponible'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowProductModal(false)}
                  className="border-[#E8DDD4]"
                >
                  Fermer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PharmacyCatalogPage;
